import React from 'react';
import { Shield, Key, Link2, Calendar, Clock, CheckCircle2, Edit2, Trash2, TestTube2 } from 'lucide-react';
import { ApiCredential } from '../../../auth/services/credentials.service';

interface CredentialCardProps {
  credential: ApiCredential;
  onTest: (credential: ApiCredential) => void;
  onEdit: (credential: ApiCredential) => void;
  onDelete: (credential: ApiCredential) => void;
  isSelected?: boolean;
  onSelect?: (credential: ApiCredential) => void;
  mode?: 'select' | 'manage';
}

const getAuthTypeLabel = (authType: string) => {
  switch (authType) {
    case 'TOKEN_SIMPLE': return 'Token Simple';
    case 'BEARER_TOKEN': return 'Bearer Token';
    case 'OAUTH_CLIENT_CREDENTIALS': return 'OAuth 2.0';
    default: return authType;
  }
};

const getAuthTypeBadge = (authType: string) => {
  switch (authType) {
    case 'OAUTH_CLIENT_CREDENTIALS':
      return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
    case 'BEARER_TOKEN':
      return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    case 'TOKEN_SIMPLE':
      return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
    default:
      return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
  }
};

const formatRelativeTime = (date: string) => {
  const now = new Date();
  const past = new Date(date);
  const diffMs = now.getTime() - past.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Ahora';
  if (diffMins < 60) return `Hace ${diffMins}m`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays < 30) return `Hace ${diffDays}d`;
  return new Date(date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
};

const maskSecret = (value: string, length: number = 8) => {
  if (!value || value.length <= 4) return '••••••••';
  return `${value.slice(0, 4)}${'•'.repeat(length)}${value.slice(-4)}`;
};

export const CredentialCard: React.FC<CredentialCardProps> = ({
  credential,
  onTest,
  onEdit,
  onDelete,
  isSelected,
  onSelect,
  mode = 'manage'
}) => {
  const handleClick = () => {
    if (mode === 'select' && onSelect) {
      onSelect(credential);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={`
        bg-slate-800 border rounded-lg transition-all duration-200
        hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/10
        ${mode === 'select' ? 'cursor-pointer' : ''}
        ${
          isSelected
            ? 'border-blue-500 shadow-lg shadow-blue-500/20'
            : 'border-slate-700'
        }
      `}
    >
      {/* Header */}
      <div className="p-5 border-b border-slate-700">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <Shield className="w-5 h-5 text-blue-400" />
              <h3 className="text-lg font-semibold text-white">{credential.name}</h3>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`px-3 py-1 text-xs font-medium rounded-full border ${
                  getAuthTypeBadge(credential.authType)
                }`}
              >
                {getAuthTypeLabel(credential.authType)}
              </span>
            </div>
          </div>

          {isSelected && (
            <div className="flex-shrink-0">
              <CheckCircle2 className="w-6 h-6 text-blue-500" />
            </div>
          )}
        </div>
      </div>

      {/* Body - Metadata */}
      <div className="p-5 space-y-3">
        {/* OAuth Token URL */}
        {credential.hasTokenUrl && credential.tokenUrlOAuth && (
          <div className="flex items-start gap-3 text-sm">
            <Link2 className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-slate-400 text-xs mb-1">Token URL</p>
              <p className="text-slate-200 font-mono text-xs truncate">
                {credential.tokenUrlOAuth}
              </p>
            </div>
          </div>
        )}

        {/* Client ID (OAuth) */}
        {credential.hasClientId && (
          <div className="flex items-start gap-3 text-sm">
            <Key className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-slate-400 text-xs mb-1">Client ID</p>
              <p className="text-slate-300 font-mono text-xs">
                {maskSecret('client-id-placeholder', 12)}
              </p>
            </div>
          </div>
        )}

        {/* Bearer Token */}
        {credential.hasBearerToken && (
          <div className="flex items-start gap-3 text-sm">
            <Key className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-slate-400 text-xs mb-1">Bearer Token</p>
              <p className="text-slate-300 font-mono text-xs">
                {maskSecret('bearer-token-placeholder', 16)}
              </p>
            </div>
          </div>
        )}

        {/* Metadata Footer */}
        <div className="flex items-center gap-4 pt-2 border-t border-slate-700 text-xs text-slate-400">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            <span>Creada: {new Date(credential.createdAt).toLocaleDateString('es-ES')}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            <span>Actualizada: {formatRelativeTime(credential.updatedAt)}</span>
          </div>
        </div>
      </div>

      {/* Actions - Solo en modo manage */}
      {mode === 'manage' && (
        <div className="p-4 bg-slate-900/50 border-t border-slate-700 flex items-center justify-end gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onTest(credential);
            }}
            className="
              flex items-center gap-2 px-4 py-2 text-sm font-medium
              bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg
              transition-colors
            "
          >
            <TestTube2 className="w-4 h-4" />
            Test
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(credential);
            }}
            className="
              flex items-center gap-2 px-4 py-2 text-sm font-medium
              bg-blue-600 hover:bg-blue-700 text-white rounded-lg
              transition-colors
            "
          >
            <Edit2 className="w-4 h-4" />
            Editar
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(credential);
            }}
            className="
              flex items-center gap-2 px-4 py-2 text-sm font-medium
              bg-red-600 hover:bg-red-700 text-white rounded-lg
              transition-colors
            "
          >
            <Trash2 className="w-4 h-4" />
            Eliminar
          </button>
        </div>
      )}
    </div>
  );
};
